import { RouteDefinition, RouteDefinitionWithParameter, routeWithoutParameters } from "@commonClient/src/utils/routes";

interface CloudAccountAwareLink {
    cloudAccount: string;
}

interface CloudAccountAndTabAwareLink {
    cloudAccount: string;
    activeTab: string;
}

interface PricingConfigurationUrlParams {
    configuration: string;
    dataTransferPercent?: number;
    startDate?: string;
    endDate?: string;
    period?: string;
}

export function stripCloudAccountName(path: string): string {
    const prefix = "/portal/";
    if (path.startsWith(prefix)) {
        const indexOf = path.indexOf("/", prefix.length);
        return path.substring(indexOf);
    }
    return "/";
}

interface CostDetailsUrlParams {
    cloudAccount: string;
    billedAsOf: string;
}

export default class AppRoutes {
    static forPublicSection(): RouteDefinition {
        return routeWithoutParameters("/", "/*");
    }

    static forPageNotFound(): RouteDefinition {
        return routeWithoutParameters("/not-found");
    }

    static forTerms(): RouteDefinition {
        return routeWithoutParameters("/terms");
    }

    static forPrivacyPolicy(): RouteDefinition {
        return routeWithoutParameters("/privacy-policy");
    }

    static forSupportPolicy(): RouteDefinition {
        return routeWithoutParameters("/support-policy");
    }

    static forPricing(): RouteDefinitionWithParameter<PricingConfigurationUrlParams> {
        const base = "/pricing";

        return {
            definition: base,
            link(values?: PricingConfigurationUrlParams): string {
                if (!values) {
                    return base;
                }
                return (
                    base +
                    "?config=" +
                    values.configuration +
                    (values.dataTransferPercent ? `&dataTransfer=${values.dataTransferPercent}` : "") +
                    (values.startDate ? `&startDate=${values.startDate}` : "") +
                    (values.endDate ? `&endDate=${values.endDate}` : "") +
                    (values.period ? `&period=${values.period}` : "")
                );
            },
            linkForRoute: base,
        };
    }

    static forPortalSection(): RouteDefinition {
        return routeWithoutParameters("/portal", "portal/*");
    }

    static forHomePage(): RouteDefinition {
        return routeWithoutParameters("/");
    }

    static forUserLoginLink(): RouteDefinition {
        return routeWithoutParameters("/user/login/link");
    }

    static forUserLogin(): RouteDefinition {
        return routeWithoutParameters("/user/login");
    }

    static forInvitation(): RouteDefinition {
        return routeWithoutParameters("/user/invitation");
    }

    static forUserLoginWith2fa(): RouteDefinition {
        return routeWithoutParameters("/user/login-with-2fa");
    }

    static forUserLoginWithRecoveryCode(): RouteDefinition {
        return routeWithoutParameters("/user/login-with-recovery-code");
    }

    static forUserLogout(): RouteDefinition {
        return routeWithoutParameters("/user/logout");
    }

    static forUserRegistration(): RouteDefinition {
        return routeWithoutParameters("/user/register");
    }

    static forRegistrationThankYou(): RouteDefinition {
        return routeWithoutParameters("/user/register/thank-you");
    }

    static forAzureMarketplaceSubscriptionRegistration(): RouteDefinition {
        return routeWithoutParameters("/azure-marketplace/register-subscription");
    }

    static forAzureMarketplaceSubscriptionRegistrationThankYou(): RouteDefinition {
        return routeWithoutParameters("/azure-marketplace/register-subscription/thank-you");
    }

    static forAwsMarketplaceSubscriptionRegistration(): RouteDefinition {
        return routeWithoutParameters("/aws-marketplace/register-customer");
    }

    static forAwsMarketplaceSubscriptionRegistrationThankYou(): RouteDefinition {
        return routeWithoutParameters("/aws-marketplace/register-customer/thank-you");
    }

    static forAzureMarketplaceSubscriptionManagerRegistration(): RouteDefinition {
        return routeWithoutParameters("/azure-marketplace/register-subscription-manager");
    }

    static forRegistrationThankYouUserAlreadyExists(): RouteDefinition {
        return routeWithoutParameters("/user/register/thank-you-login-exists");
    }

    private static routeForPortal(url: string) {
        return {
            definition: "/portal/:cloudAccount/" + url,
            link: (values: CloudAccountAwareLink) => "/portal/" + encodeURIComponent(values.cloudAccount) + "/" + url,
            linkForRoute: "/:cloudAccount/" + url,
        };
    }

    static forBackups(): RouteDefinitionWithParameter<CloudAccountAwareLink> {
        return AppRoutes.routeForPortal("backups");
    }

    static forBilling(): RouteDefinitionWithParameter<CloudAccountAwareLink> {
        return AppRoutes.routeForPortal("billing/summary");
    }

    static forApiKeys(): RouteDefinitionWithParameter<CloudAccountAwareLink> {
        return AppRoutes.routeForPortal("apiKeys");
    }

    static forBillingDetails(): RouteDefinitionWithParameter<CostDetailsUrlParams> {
        const base = AppRoutes.routeForPortal("billing/cost-details");

        return {
            definition: base.definition + "/:billedAsOf",
            link(values: CloudAccountAwareLink & { billedAsOf: string }): string {
                return base.link(values) + "/" + encodeURIComponent(values.billedAsOf);
            },
            linkForRoute: base.linkForRoute + "/:billedAsOf",
        };
    }

    static forInvoiceDetails(): RouteDefinitionWithParameter<{ cloudAccount: string; invoiceId: string }> {
        const base = AppRoutes.routeForPortal("billing/invoice-details");
        return {
            definition: base.definition + "/:invoiceId",
            link(values: { cloudAccount: string; invoiceId: string }): string {
                return base.link(values) + "/" + values.invoiceId;
            },
            linkForRoute: base.linkForRoute + "/:invoiceId",
        };
    }

    static forProductsList(): RouteDefinitionWithParameter<CloudAccountAwareLink> {
        return AppRoutes.routeForPortal("products");
    }

    static forAccount(): RouteDefinitionWithParameter<CloudAccountAwareLink> {
        return AppRoutes.routeForPortal("account");
    }

    static forProfile(): RouteDefinition {
        return routeWithoutParameters("/portal/profile");
    }

    static forSupport(): RouteDefinitionWithParameter<CloudAccountAwareLink> {
        return AppRoutes.routeForPortal("support");
    }

    static forProductCreate(): RouteDefinitionWithParameter<CloudAccountAwareLink> {
        return AppRoutes.routeForPortal("product/create");
    }

    static forProductDetails(): RouteDefinitionWithParameter<CloudAccountAndTabAwareLink & { id: string }> {
        const base = AppRoutes.routeForPortal("product/");

        return {
            definition: base.definition + ":activeTab/" + ":id",
            link(values: CloudAccountAndTabAwareLink & { id: string }): string {
                return base.link(values) + `${values.activeTab}/` + encodeURIComponent(values.id);
            },
            linkForRoute: base.linkForRoute + ":activeTab/" + ":id",
        };
    }

    static forProfileEnableAuthenticator(): RouteDefinition {
        return routeWithoutParameters("/portal/profile/enable-authenticator");
    }

    static forUserGenerateRecoveryCodes(): RouteDefinition {
        return routeWithoutParameters("/portal/profile/generate-recovery-codes");
    }

    static forUserDisableTwoFactor(): RouteDefinition {
        return routeWithoutParameters("/portal/profile/disable-2fa");
    }

    static forStatus(): RouteDefinition {
        return routeWithoutParameters("/status");
    }

    static forInvitationLink() {
        return routeWithoutParameters("/invitation/accept");
    }

    static forHome() {
        return AppRoutes.routeForPortal("home");
    }

    static forAccessDenied(): RouteDefinition {
        return routeWithoutParameters("/portal");
    }
}
